/**
 * Consent modal shown on first entry into Resume Studio.
 *
 * Fetches the active `resume_studio` policy row from
 * `/api/policies/resume_studio`, renders it as plain paragraphs (the
 * server returns markdown-flavoured plain text; we honor line breaks
 * but do NOT execute embedded HTML), and POSTs to `/api/me/consents`
 * with `{policy_type:"resume_studio", version}` when the user agrees.
 *
 * The consent record is user-scoped (backend derives user_id from the
 * JWT). No sensitive data is captured beyond the version + timestamp.
 */
import { useEffect, useState } from "react";
import api, { extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck } from "lucide-react";

export function ResumeStudioConsentModal({ open, onConsented, onCancel }) {
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    api.get("/policies/resume_studio")
      .then((r) => setPolicy(r.data))
      .catch((err) => setError(extractApiError(err, "Failed to load consent.")));
  }, [open]);

  const accept = async () => {
    if (!policy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/me/consents", {
        policy_type: "resume_studio",
        version:     policy.version,
      });
      onConsented?.();
    } catch (err) {
      setError(extractApiError(err, "Failed to record consent."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent
        data-testid="resume-studio-consent-modal"
        className="max-w-2xl bg-bg-surface border-border-subtle text-fg-primary"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-progress-teal" aria-hidden="true" />
            Resume Studio consent
          </DialogTitle>
          <DialogDescription className="text-fg-secondary">
            One-time consent — needed once before you can create, edit, or import.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <Alert variant="destructive" data-testid="resume-studio-consent-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {!policy ? (
          <p className="text-sm text-fg-secondary py-8 text-center"
             data-testid="resume-studio-consent-loading">
            Loading the current consent text…
          </p>
        ) : (
          <div
            data-testid="resume-studio-consent-body"
            className="prose prose-invert prose-sm max-w-none max-h-[50vh] overflow-y-auto pr-3 border border-border-subtle rounded-md p-4 bg-bg-base whitespace-pre-line text-sm leading-relaxed"
          >
            {policy.content}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2 sm:justify-end">
          <Button
            variant="ghost"
            onClick={onCancel}
            data-testid="resume-studio-consent-cancel-btn"
            className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
          >
            Not now
          </Button>
          <Button
            onClick={accept}
            disabled={!policy || busy}
            data-testid="resume-studio-consent-accept-btn"
            className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
          >
            {busy ? "Recording…" : (policy?.version ? `I agree to v${policy.version}` : "I agree")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ResumeStudioConsentModal;
